import { supabase } from './supabase.js';
import { toCamel, toSnake } from './case.js';

/**
 * Fetch all tasks visible to the current user.
 * RLS handles filtering — we just request and receive what we're allowed to see.
 */
export async function fetchTasks() {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .is('deleted_at', null)
    .eq('completed', false)
    .order('created_at', { ascending: false });
  if (error) {
    console.error('fetchTasks error:', error);
    return [];
  }
  return toCamel(data);
}

/**
 * Create a new task.
 * Accepts the prototype's task shape (camelCase, list, assignedTo, etc.)
 * and writes it to the database.
 */
export async function createTask(taskData) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not logged in');

  // Map prototype fields to database columns
  const row = {
    created_by: user.id,
    title: taskData.title,
    category_id: taskData.categoryId || 'daily',
    list: taskData.list,
    deadline: taskData.deadline || null,
    repeat_type: taskData.repeatType || 'none',
    repeat_interval: taskData.interval || null,
    repeat_month_day: taskData.monthDay || null,
    repeat_weekday: taskData.weekday !== undefined ? taskData.weekday : null,
    repeat_weekdays: Array.isArray(taskData.weekdays) && taskData.weekdays.length > 0
      ? taskData.weekdays
      : (taskData.weekday !== undefined && taskData.weekday !== null ? [taskData.weekday] : null),
    pair_id: taskData.pairId || null,
    assigned_to: taskData.assignedTo || null,
    tag_points: taskData.tagPoints || 5,
    message: taskData.message || null,
    message_hidden: taskData.messageHidden || false,
    reward: taskData.reward || null,
    reward_emoji: taskData.rewardEmoji || null,
    reward_hidden: taskData.rewardHidden || false,
  };

  const { data, error } = await supabase
    .from('tasks')
    .insert(row)
    .select()
    .single();
  if (error) {
    console.error('createTask error:', error);
    throw error;
  }
  return toCamel(data);
}

/**
 * Update an existing task.
 * Uses explicit field mapping (same as createTask) to avoid camelCase/snake_case mismatches.
 */
export async function updateTask(taskId, updates) {
  // Build the row with only the fields the caller provided.
  // Each app-side field maps explicitly to its database column.
  const row = {};
  if ('title' in updates) row.title = updates.title;
  if ('categoryId' in updates) row.category_id = updates.categoryId;
  if ('list' in updates) row.list = updates.list;
  if ('deadline' in updates) row.deadline = updates.deadline;
  if ('repeatType' in updates) row.repeat_type = updates.repeatType;
  if ('interval' in updates) row.repeat_interval = updates.interval;
  if ('monthDay' in updates) row.repeat_month_day = updates.monthDay;
  if ('weekday' in updates) row.repeat_weekday = updates.weekday;
  if ('weekdays' in updates) {
    row.repeat_weekdays = Array.isArray(updates.weekdays) && updates.weekdays.length > 0
      ? updates.weekdays
      : null;
    // Keep singular column in sync (first weekday) for backward compat
    if (Array.isArray(updates.weekdays) && updates.weekdays.length > 0) {
      row.repeat_weekday = updates.weekdays[0];
    }
  }
  if ('assignedTo' in updates) row.assigned_to = updates.assignedTo;
  if ('pairId' in updates) row.pair_id = updates.pairId;
  if ('tagPoints' in updates) row.tag_points = updates.tagPoints;
  if ('message' in updates) row.message = updates.message;
  if ('messageHidden' in updates) row.message_hidden = updates.messageHidden;
  if ('reward' in updates) row.reward = updates.reward;
  if ('rewardEmoji' in updates) row.reward_emoji = updates.rewardEmoji;
  if ('rewardHidden' in updates) row.reward_hidden = updates.rewardHidden;
  if ('completed' in updates) row.completed = updates.completed;

  const { data, error } = await supabase
    .from('tasks')
    .update(row)
    .eq('id', taskId)
    .select()
    .single();
  if (error) {
    console.error('updateTask error:', error);
    throw error;
  }
  return toCamel(data);
}

/**
 * Delete a task.
 *
 * Note: We use a hard delete here. We initially tried soft-delete (UPDATE
 * setting deleted_at), but RLS WITH CHECK rejects updates that make the
 * row invisible via SELECT (which has `deleted_at IS NULL` filter). To
 * support soft-delete properly we'd need a separate "trash"-policy.
 *
 * Hard delete is fine for v1 — when a task is gone, it's gone. The
 * completion record (in completions table) remains as historical record.
 */
export async function deleteTask(taskId) {
  const { error } = await supabase
    .from('tasks')
    .delete()
    .eq('id', taskId);
  if (error) {
    console.error('deleteTask error:', error);
    throw error;
  }
}

/**
 * Mark a task as completed.
 * For non-recurring tasks: sets completed=true.
 * For recurring tasks: advances the deadline to the next occurrence.
 *
 * Also creates a completion record (used for stats and history).
 */
export async function completeTask(taskId, completionMeta = {}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not logged in');

  // Fetch the task first
  const { data: task, error: fetchError } = await supabase
    .from('tasks')
    .select('*')
    .eq('id', taskId)
    .single();
  if (fetchError) throw fetchError;

  // Create completion record
  const completionRow = {
    task_id: taskId,
    pair_id: task.pair_id,
    completed_by: user.id,
    task_title: task.title,
    task_list: task.list,
    points_earned: completionMeta.points || 0,
    was_late: completionMeta.wasLate || false,
    was_early: completionMeta.wasEarly || false,
  };
  const { error: completionError } = await supabase
    .from('completions')
    .insert(completionRow);
  if (completionError) {
    console.error('completion insert error:', completionError);
    throw completionError;
  }

  // For non-recurring: mark completed
  // For recurring: advance deadline (we let the caller compute new deadline)
  if (task.repeat_type === 'none' || !task.repeat_type) {
    const { error: updateError } = await supabase
      .from('tasks')
      .update({ completed: true })
      .eq('id', taskId);
    if (updateError) throw updateError;
  } else if (completionMeta.nextDeadline) {
    const { error: updateError } = await supabase
      .from('tasks')
      .update({ deadline: completionMeta.nextDeadline })
      .eq('id', taskId);
    if (updateError) throw updateError;
  }

  return toCamel(task);
}

/**
 * Fetch recent completions for a pair (or for current user if no pair).
 * Used for "Løst i dag" sections and stats.
 */
export async function fetchRecentCompletions(limit = 50) {
  const { data, error } = await supabase
    .from('completions')
    .select('*')
    .order('completed_at', { ascending: false })
    .limit(limit);
  if (error) {
    console.error('fetchRecentCompletions error:', error);
    return [];
  }
  return toCamel(data);
}
